import React, { useEffect, useMemo, useRef, useState } from "react";
import { animate, remove } from "animejs";
import { Link } from "react-router-dom";
import { setServiceCategory } from "../states/navigationState";

import L3 from "../assets/logo/paperLogoSM.png";
import L1 from "../assets/logo/eventsLogoSM.png";
import L2 from "../assets/logo/digitalLogoSM.png";
import { useDispatch, useSelector } from "react-redux";

const OrbitScroll = ({ scrollContainerRef }) => {
  const dispatch = useDispatch();
  const isIta = useSelector((state) => state.createUser.languageIta);
  const sectionRef = useRef(null);
  const ringRef = useRef(null);
  const wrapperRef = useRef(null);


  // refs per le icone: counter-rotation + scale
  const iconRefs = useRef([null, null, null]);

  // pannello servizi
  const panelRef = useRef(null);
  const [active, setActive] = useState(0);

  //evita che i pannelli si blocchino
  const panelContentRef = useRef(null);
  const panelIsEnteringRef = useRef(false);

  // quale icona è attiva (0..2) oppure -1 = nessuna
  const activeRef = useRef(-1);

  // pannello: entra solo al primo scroll
  const hasStartedRef = useRef(false);

  // Intro
  const introRef = useRef(null);
  const introHiddenRef = useRef(false);

  // Mobile detection
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 768px)").matches
      : false
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 768px)");
    const handler = (e) => setIsMobile(e.matches);
    handler(mq);
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, []);

  // Animazione dello slogan intro (all'apertura)
  useEffect(() => {
    const el = introRef.current;
    if (!el) return;

    // se hai già scrollato, non ri-animare in caso di resize mobile/desktop
    if (hasStartedRef.current) return;

    el.style.display = "block";
    el.style.opacity = "0";
    el.style.transform = "translateY(14px)";

    remove(el);
    animate(el, {
      opacity: [0, 1],
      translateY: [14, 0],
      duration: 520,
      easing: "easeOutExpo",
      delay: 120,
    });

    return () => remove(el);
  }, [isMobile]);

  // Rotazione "su sé stessa" del contenuto di ogni icona
  const ICON_SELF_ROT = [0, 0, 0];

  // mappa icona -> services[]
  const ICON_TO_SERVICE = [0, 1, 2];

  const services = useMemo(
    () => [
      {
        titleIta: "Eventi",
        titleEng: "Events",
        itemsIta: [
          "Affianchiamo i nostri clienti nelle principali fiere dentali e medicali, garantendo visibilità, posizionamento strategico e una gestione completa dell’organizzazione per una presenza efficace e senza pensieri.",
        ],
        itemsEng: [
          "We stand alongside our clients at the major dental and medical trade shows, delivering visibility, strategic positioning, and full organizational support for a seamless and impactful presence.",
        ],
        index: 1,
        link: "Events",
        color: "#e82283"
      },
      {
        titleIta: "Digitale",
        titleEng: "Digital",
        itemsIta: [
          "Con strategie mirate raggiungiamo distributori, dentisti e laboratori odontotecnici in tutto il mondo, trasformando la vostra visibilità online in contatti concreti e risultati misurabili.",
        ],
        itemsEng: [
          "Through targeted strategies, we connect you with distributors, dentists, and dental laboratories worldwide, turning your online visibility into real leads and measurable results.",
        ],
        index: 2,
        link: "Digital",
        color: "#3375c7"
      },
      {
        titleIta: "Riviste",
        titleEng: "Publishing",
        itemsIta: [
          "Dal 1994, Infodent International è un punto di riferimento nell’editoria dentale. In oltre 30 anni abbiamo distribuito più di 2 milioni di copie nelle principali fiere di settore e portato nel mondo 7 riviste di eccellenza tra ambito dentale e medicale.",
        ],
        itemsEng: [
          "Since 1994, Infodent International has been a leading reference in dental publishing. For over 30 years, we have distributed more than 2 million copies at major industry exhibitions and delivered 7 high-quality dental and medical publications worldwide.",
        ],
        index: 0,
        link: "Paper",
        color: "#14a7ad"
      },
    ],
    []
  );

  const items = isIta ? services[active].itemsIta : services[active].itemsEng;

  useEffect(() => {
    let rafId = 0;
    let cleanup = () => { };

    const ring = ringRef.current;
    const section = sectionRef.current;
    const wrapper = wrapperRef.current;
    if (!ring || !section || !wrapper) return;

    const setup = () => {
      const scroller = scrollContainerRef?.current;
      if (!scroller) {
        rafId = requestAnimationFrame(setup);
        return;
      }

      // angoli base icone (in gradi) rispetto al centro (100,100)
      const baseAngles = [-90, 30.6, 149.4];
      const norm360 = (a) => ((a % 360) + 360) % 360;

      // offset ring mobile (IMPORTANT: deve valere per ring + counter + attivazione)
      const RING_OFFSET_DEG = isMobile ? 120 : 0;

      // Punto attivazione testo
      const ACTIVATION_ANGLE_DEG = isMobile ? 0 : 20;

      const angDist = (a, target) => {
        const d = norm360(a - target);
        return Math.min(d, 360 - d);
      };

      const WINDOW_DEG = isMobile ? 24 : 18;
      const PANEL_START_THRESHOLD = 0.15;

      const animateActive = (nextIndex) => {
        if (activeRef.current === nextIndex) return;

        const wrapperEl = wrapperRef.current;
        const panelEl = panelRef.current;

        activeRef.current = nextIndex;

        const panelContentEl = panelContentRef.current;
        if (wrapperEl) remove(wrapperEl);
        iconRefs.current.forEach((el) => el && remove(el));

        // wrapper shrink/move SOLO se c'è un'icona attiva E dopo start
        if (wrapperEl) {
          if (hasStartedRef.current && nextIndex !== -1) {
            animate(wrapperEl, {
              translateY: isMobile ? -18 : -60,
              scale: isMobile ? 0.92 : 0.86,
              duration: 520,
              easing: "easeOutExpo",
            });
          } else {
            animate(wrapperEl, {
              translateY: 0,
              scale: 1,
              duration: 420,
              easing: "easeOutExpo",
            });
          }
        }

        // scale icone
        iconRefs.current.forEach((el, i) => {
          if (!el) return;
          animate(el, {
            scale: i === nextIndex ? 1.35 : 1,
            duration: 280,
            easing: "easeOutExpo",
          });
        });

        // aggiorna testo solo se index valido
        if (nextIndex !== -1) {
          setActive(ICON_TO_SERVICE[nextIndex]);

          if (panelContentEl && hasStartedRef.current && !panelIsEnteringRef.current) {
            // forza stato iniziale “micro” (così non resta mai mezzo)
            panelContentEl.style.opacity = "0.6";
            panelContentEl.style.transform = "translateY(6px)";

            animate(panelContentEl, {
              opacity: [0.6, 1],
              translateY: [6, 0],
              duration: 220,
              easing: "easeOutCubic",
            });
          }
        }
      };

      const update = () => {
        rafId = 0;

        const sectionOffsetTop = section.offsetTop;
        const p = (scroller.scrollTop - sectionOffsetTop) / scroller.clientHeight;
        const clamped = Math.max(0, Math.min(1, p));

        const panelEl = panelRef.current;
        const introEl = introRef.current;

        // pannello nascosto finché non inizi a scrollare
        if (!hasStartedRef.current && panelEl) {
          panelEl.style.opacity = "0";
          panelEl.style.transform = "translateY(240px)";
          panelEl.style.pointerEvents = "none";
        }

        // entra SOLO al primo scroll
        if (!hasStartedRef.current && clamped > PANEL_START_THRESHOLD) {
          hasStartedRef.current = true;

          // 1) nascondi intro (una volta)
          if (introEl && !introHiddenRef.current) {
            introHiddenRef.current = true;
            remove(introEl);

            animate(introEl, {
              opacity: [1, 0],
              translateY: [0, -10],
              duration: 260,
              easing: "easeOutCubic",
              complete: () => {
                introEl.style.display = "none";
              },
            });
          }

          // 2) entra pannello
          if (panelEl) {
            panelIsEnteringRef.current = true;   // blocco interferenze
            remove(panelEl);
            panelEl.style.pointerEvents = "auto";

            animate(panelEl, {
              opacity: [0, 1],
              translateY: [240, 0],
              duration: 520,
              delay: 160,
              easing: "easeOutExpo",
              complete: () => {
                panelIsEnteringRef.current = false; // 🔓 ok, adesso si può cambiare testo
                panelEl.style.transform = "translateY(0px)";
                panelEl.style.opacity = "1";
              },
            });
          }
        }

        // rotazione ring
        const deg = clamped * 360;
        const degTotal = deg + RING_OFFSET_DEG;
        ring.style.transform = `rotateZ(${degTotal}deg)`;

        // counter-rotation icone (usa degTotal!)
        iconRefs.current.forEach((el) => {
          if (!el) return;
          animate(el, {
            rotate: -degTotal,
            duration: 16,
            easing: "linear",
          });
        });

        // attivazione icona più vicina al punto target (usa degTotal!)
        let bestIdx = -1;
        let bestDist = 999;

        for (let i = 0; i < baseAngles.length; i++) {
          const a = norm360(baseAngles[i] + degTotal);
          const d = angDist(a, ACTIVATION_ANGLE_DEG);
          if (d < bestDist) {
            bestDist = d;
            bestIdx = i;
          }
        }

        // non attivare finché non parte
        const allowActivation = hasStartedRef.current && !panelIsEnteringRef.current;
        const nextIndex = allowActivation && bestDist <= WINDOW_DEG ? bestIdx : -1;

        animateActive(nextIndex);
      };

      const onScroll = () => {
        if (rafId) return;
        rafId = requestAnimationFrame(update);
      };

      update();
      scroller.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", onScroll);

      cleanup = () => {
        scroller.removeEventListener("scroll", onScroll);
        window.removeEventListener("resize", onScroll);
      };
    };

    setup();

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      cleanup();
    };
  }, [scrollContainerRef, isMobile]);

  // SVG sizes
  const logoSize = 30;
  const half = logoSize / 2;

  return (
    <section ref={sectionRef} style={{ position: "relative", height: "200vh" }}>
      <div
        className={`myBgDark text-light ${isMobile ? "d-flex flex-column pt-5" : ""}`}
        style={{
          position: "sticky",
          top: 0,
          height: "100vh",
          display: "grid",
          alignItems: "center",
          justifyItems: "center",
          perspective: "900px",
          padding: isMobile ? "14px 0" : 0,
        }}
      >
        <div
          style={{
            width: "min(980px, 92vw)",
            display: "grid",
            gridTemplateColumns: isMobile
              ? "1fr"
              : "minmax(320px, 620px) minmax(280px, 1fr)",
            gap: isMobile ? 25 : "clamp(16px, 4vw, 44px)",
            alignItems: "center",
            justifyItems: "center",
          }}
        >
          {/* Cerchio */}
          <div
            ref={wrapperRef}
            style={{
              width: isMobile ? "min(230px, 70vw)" : "min(520px, 90vw)",
              height: isMobile ? "min(230px, 70vw)" : "min(520px, 90vw)",
              transformStyle: "preserve-3d",
              transform: "rotateX(0deg) rotateZ(0deg)",
              willChange: "transform",
            }}
          >
            <div
              ref={ringRef}
              style={{
                width: "100%",
                height: "100%",
                transformOrigin: "50% 50%",
                willChange: "transform",
              }}
            >
              <svg viewBox="0 0 200 200" width="100%" height="100%" style={{ overflow: "visible" }}>
                <circle
                  cx="100"
                  cy="100"
                  r="82"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1"
                  opacity="0.35"
                />

                {/* Logo 1 */}
                <g transform="translate(100 18)">
                  <g
                    ref={(el) => (iconRefs.current[0] = el)}
                    style={{ transformBox: "fill-box", transformOrigin: "center" }}
                  >
                    <g transform={`rotate(${ICON_SELF_ROT[0]} 0 0)`}>
                      <circle r="22" opacity="0.12" />
                      <Link to={"/ourservices"} onClick={() => dispatch(setServiceCategory("Events"))}>
                        <image
                          href={L1}
                          x={-half}
                          y={-half}
                          width={logoSize}
                          height={logoSize}
                          preserveAspectRatio="xMidYMid meet"
                        />
                      </Link>
                    </g>
                  </g>
                </g>

                {/* Logo 2 */}
                <g transform="translate(176 145)">
                  <g
                    ref={(el) => (iconRefs.current[1] = el)}
                    style={{ transformBox: "fill-box", transformOrigin: "center" }}
                  >
                    <g transform={`rotate(${ICON_SELF_ROT[1]} 0 0)`}>
                      <circle r="22" opacity="0.12" />
                      <Link to={"/ourservices"} onClick={() => dispatch(setServiceCategory("Digital"))}>
                        <image
                          href={L2}
                          x={-half}
                          y={-half}
                          width={logoSize}
                          height={logoSize}
                          preserveAspectRatio="xMidYMid meet"
                        />
                      </Link>
                    </g>
                  </g>
                </g>

                {/* Logo 3 */}
                <g transform="translate(24 145)">
                  <g
                    ref={(el) => (iconRefs.current[2] = el)}
                    style={{ transformBox: "fill-box", transformOrigin: "center" }}
                  >
                    <g transform={`rotate(${ICON_SELF_ROT[2]} 0 0)`}>
                      <circle r="22" opacity="0.12" />
                      <Link to={"/ourservices"} onClick={() => dispatch(setServiceCategory("Paper"))}>
                        <image
                          href={L3}
                          x={-half}
                          y={-half}
                          width={logoSize}
                          height={logoSize}
                          preserveAspectRatio="xMidYMid meet"
                        />
                      </Link>
                    </g>
                  </g>
                </g>
              </svg>
            </div>
          </div>

          {/* TEXT SLOT (evita reflow/scatti): intro e panel sovrapposti */}
          <div
            style={{
              position: "relative",
              maxWidth: 520,
              width: isMobile ? "min(520px, 92vw)" : "100%",
              minHeight: isMobile ? 240 : 280,
            }}
          >
            {/* INTRO */}
            <div
              ref={introRef}
              className="d-flex align-items-center justify-content-center"
              style={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                opacity: 0,
                transform: "translateY(14px)",
                textAlign: isMobile ? "center" : "left",
                willChange: "transform, opacity",
              }}
            >
              <div
                style={{
                  fontSize: isMobile ? 24 : 32,
                  fontWeight: 800,
                  marginTop: 0,
                  lineHeight: 1.15,
                }}
              >
                <div className="floatLine lineA myTextFucsia">
                  Build Connections
                </div>

                <div className="floatLine lineB myTextAcqua">
                  Boost Visibility
                </div>
              </div>
            </div>

            {/* PANEL */}
            <div
              ref={panelRef}
              style={{
                position: "absolute",
                inset: 0,
                opacity: 0,
                transform: "translateY(240px)",
                pointerEvents: "none",
                willChange: "transform, opacity",
                textAlign: isMobile ? "center" : "left",
              }}
            >
              <div ref={panelContentRef}>

                <div style={{ fontSize: 12, letterSpacing: 2, opacity: 0.7 }}>
                  {isIta ? "SERVIZI" : "SERVICES"}
                </div>

                <div
                  style={{
                    fontSize: 28,
                    fontWeight: 700,
                    marginTop: 6,
                    color: services[active].color,
                  }}
                >
                  {isIta ? services[active].titleIta : services[active].titleEng}
                </div>

                <div style={{ marginTop: 14, lineHeight: 1.7 }}>
                  {
                    items.map((t) => (
                      <p key={t} style={{ fontSize: 16, opacity: 0.9, margin: 0 }}>
                        {t}
                      </p>
                    ))
                  }
                </div>

                <div style={{ marginTop: 18, fontSize: 14, opacity: 0.75 }}>
                  <Link style={{ color: `${services[active].color}` }} onClick={() => dispatch(setServiceCategory(services[active].link))} to={"/ourservices"}>{isIta ? `esplora i nostr servizi ${services[active].titleIta}` : `explore our ${services[active].titleEng} services`}</Link>
                </div>

              </div>

            </div>
          </div>
          {/* END TEXT SLOT */}
        </div>
      </div>
    </section>
  );
};

export default OrbitScroll;